import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import JoditEditor from 'jodit-react';
import { createJob, getCategories, getJobById, updateJob } from '../../../services/admin/jobService';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { toast } from 'react-toastify';

const JobForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const isEdit = Boolean(id);
  const editor = useRef(null);
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skillInput, setSkillInput] = useState('');
  const [locationArea, setLocationArea] = useState(['', '', 'India']); // [country, state, city]

  const [formData, setFormData] = useState({
    // Basic Information
    title: '',
    shortDescription: '',
    fullDescription: '',
    category: '',
    company: '',
    employerId: '',
    
    // Location
    location: {
      area: ['', '', 'India'],
      displayName: 'India'
    },
    
    // Job Details
    contractType: 'Full-time',
    workMode: 'On-site',
    salary: {
      min: 0,
      max: 0,
      isPredicted: '0',
      currency: 'INR'
    },
    
    // Requirements
    experience: {
      min: '',
      max: '',
      unit: 'years'
    },
    applyEmail: '',
    education: '',
    skills: [],
    
    // Status & Settings
    status: 'active',
    redirectUrl: '',
    source: 'hunarpool',
    isFeatured: false,
    isUrgentHiring: false
  });

  // Fetch categories on component mount
  useEffect(() => {
    if(isEdit) {
      fetchJobDetails();
    }
    fetchCategories();
  }, []);

  // Implement fetching job details by ID and populate formData
  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      const response = await getJobById(id);
      if (response && response.data) {
        const job = response.data;
        setFormData({
          title: job.title || '',
          shortDescription: job.description || '',
          fullDescription: job.fullDescription || '',
          category: job.category._id || '',
          company: job.company || '',
          employerId: job.employerId || '',
          location: job.location || { area: ['', '', 'India'], displayName: '' },
          contractType: job.contractType || 'Full-time',
          workMode: job.workMode || 'On-site',
          salary: job.salary || { min: 0, max: 0, isPredicted: '0', currency: 'INR' },
          experience: job.experience || { min: '', max: '', unit: 'years' },
          education: job.education || '',
          applyEmail: job.applyEmail || '',
          skills: job.skills || [],
          status: job.status || 'active',
          redirectUrl: job.redirectUrl || '',
          source: job.source || 'hunarpool',
          isFeatured: job.isFeatured || false,
          isUrgentHiring: job.isUrgentHiring || false
        });
        setLocationArea(job.location?.area || ['', '', 'India']);
        
      } else {
        toast.error('Failed to fetch job details. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
    }
  };
      

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await getCategories();
      //console.log('Fetched categories:', response);
      if (response && response.data) {
        setCategories(response.data);

        if(isEdit) {
          // Mark the category as selected
          const jobCategory = response.data.find(cat => cat._id === formData.category);          
          if (jobCategory) {
            setFormData(prev => ({
              ...prev,
              category: jobCategory._id
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Jodit Editor configuration
  const config = useMemo(() => ({
    readonly: false,
    height: 400,
    toolbarAdaptive: false,
    toolbarSticky: true,
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'ul', 'ol', '|',
      'outdent', 'indent', '|',
      'font', 'fontsize', 'brush', '|',
      'align', '|',
      'link', '|',
      'symbol', 'table', '|',
      'undo', 'redo', '|',
      'find', 'selectall', '|',
      'preview', 'print'
    ],
    removeButtons: ['image', 'file', 'video'],
    showPlaceholder: false,
  }), []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleLocationChange = (index, value) => {
    const newArea = [...locationArea];
    newArea[index] = value;
    setLocationArea(newArea);
    
    // Update formData
    setFormData(prev => ({
      ...prev,
      location: {
        ...prev.location,
        area: newArea,
        displayName: newArea.filter(item => item.trim() !== '').join(', ')
      }
    }));
  };

  const handleDescriptionChange = (newContent) => {
    setFormData(prev => ({
      ...prev,
      fullDescription: newContent
    }));    
  };

  const handleSkillAdd = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  const handleSkillRemove = (skillToRemove) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSkillAdd();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const jobData = {
        ...formData,
        description: formData.shortDescription
      };
      //console.log('Submitting job data:', jobData);

      const response = isEdit ? await updateJob(id, jobData) : await createJob(jobData);
      if (response && response.data) {
        toast.success(response.message);
        navigate('/hpanel/jobs');
      } else {
        toast.error('Failed to create job. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting job:', error);
      toast.error(error.response?.data?.message || 'Error creating job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contractTypes = [
    { value: 'Full-time', label: 'Full Time' },
    { value: 'Part-time', label: 'Part Time' },
    { value: 'Contract', label: 'Contract' },
    { value: 'Permanent', label: 'Permanent' },
    { value: 'Internship', label: 'Internship' },
    { value: 'Freelance', label: 'Freelance' }
  ];

  const workModes = [
    { value: 'On-site', label: 'On Site' },
    { value: 'Remote', label: 'Remote' },
    { value: 'Hybrid', label: 'Hybrid' }
  ];

  const currencies = [
    { value: 'USD', label: 'USD ($)' },
    { value: 'INR', label: 'INR (₹)' },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'GBP', label: 'GBP (£)' }
  ];

  const experienceUnits = [
    { value: 'years', label: 'Years' },
    { value: 'months', label: 'Months' }
  ];

  return (
    <div>
      {/* SEO Settings */}
      <title>{`Add/Edit Job | ${process.env.REACT_APP_NAME}`}</title>
      <meta name="robots" content="noindex, nofollow" />

      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white">
              {isEdit ? 'Edit Job' : 'Post New Job'}
            </h2>
            <p className="text-gray-400">
              {isEdit ? 'Update job information' : 'Create a new job'}
            </p>
          </div>
          <button
            onClick={() => navigate('/hpanel/jobs')}
            className="text-gray-400 hover:text-white flex items-center"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Jobs
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information Card */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Job Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., Senior Frontend Developer"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
                disabled={loading}
              >
                <option value="">Select Category</option>
                {categories.map(category => (
                  <option key={category._id} value={category._id}>{category.categoryName}</option>
                ))}
              </select>
              {loading && <p className="text-xs text-gray-500 mt-1">Loading categories...</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company *</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Company name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Education Requirement</label>
              <input
                type="text"
                name="education"
                value={formData.education}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., Bachelor's degree in Computer Science"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
              <input
                type="text"
                value={locationArea[0]}
                onChange={(e) => handleLocationChange(0, e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., Ludhiana"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">State/Province</label>
              <input
                type="text"
                value={locationArea[1]}
                onChange={(e) => handleLocationChange(1, e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., Punjab"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
              <input
                type="text"
                value={locationArea[2]}
                onChange={(e) => handleLocationChange(2, e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., India"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Location Display Name *</label>
            <input
              type="text"
              value={formData.location.displayName}
              onChange={(e) => handleNestedChange('location', 'displayName', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., Ludhiana, Punjab or Remote"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              This will be displayed in job listings
            </p>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Short Description *</label>
            <textarea
              name="shortDescription"
              value={formData.shortDescription}
              onChange={handleInputChange}
              rows="3"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Brief description that will appear in job listings"
              required
            />
          </div>
        </div>

        {/* Job Description Card */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Job Description</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Description *</label>
            <JoditEditor
              ref={editor}
              value={formData.fullDescription}
              config={config}
              tabIndex={1}
              onChange={handleDescriptionChange}
            />
          </div>
        </div>

        {/* Skills Card */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Required Skills</h3>
          <div>
            <div className="flex mb-4">
              <input
                type="text"
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 border border-gray-300 rounded-l-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Add a skill (e.g., React, Python, Data Analysis)"
              />
              <button
                type="button"
                onClick={handleSkillAdd}
                className="btn-primary text-white px-4 py-2 rounded-r-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Add
              </button>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {formData.skills.map((skill, index) => (
                <div
                  key={index}
                  className="inline-flex items-center bg-gray-100 text-gray-800 px-3 py-1 rounded-full"
                >
                  <span>{skill}</span>
                  <button
                    type="button"
                    onClick={() => handleSkillRemove(skill)}
                    className="ml-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    &times;
                  </button>
                </div>
              ))}
              {formData.skills.length === 0 && (
                <p className="text-gray-500 italic">No skills added yet. Add some skills for this job.</p>
              )}
            </div>
          </div>
        </div>

        {/* Job Details Card */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Job Details</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contract Type *</label>
              <select
                name="contractType"
                value={formData.contractType}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              >
                {contractTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Work Mode *</label>
              <select
                name="workMode"
                value={formData.workMode}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              >
                {workModes.map(mode => (
                  <option key={mode.value} value={mode.value}>{mode.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
              <select
                value={formData.salary.currency}
                onChange={(e) => handleNestedChange('salary', 'currency', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {currencies.map(currency => (
                  <option key={currency.value} value={currency.value}>{currency.label}</option>
                ))}
              </select>
            </div>
          
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Min Salary</label>
              <input
                type="number"
                value={formData.salary.min}
                onChange={(e) => handleNestedChange('salary', 'min', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="50000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Salary</label>
              <input
                type="number"
                value={formData.salary.max}
                onChange={(e) => handleNestedChange('salary', 'max', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="80000"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Min Experience</label>
              <input
                type="number"
                value={formData.experience.min}
                onChange={(e) => handleNestedChange('experience', 'min', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Experience</label>
              <input
                type="number"
                value={formData.experience.max}
                onChange={(e) => handleNestedChange('experience', 'max', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="5"
              />
            </div>          

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Experience Unit</label>
              <select
                value={formData.experience.unit}
                onChange={(e) => handleNestedChange('experience', 'unit', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {experienceUnits.map(unit => (
                  <option key={unit.value} value={unit.value}>{unit.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Apply Email</label>
              <input
                type="email"
                name="applyEmail"
                value={formData.applyEmail}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., apply@company.com"                
              />
            </div>
          </div>
        </div>

        {/* Settings Card */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Redirect URL</label>
              <input
                type="url"
                name="redirectUrl"
                value={formData.redirectUrl}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="https://example.com/apply"
              />
              <p className="text-xs text-gray-500 mt-1">
                If provided, applicants will be redirected to this URL instead of applying on HunarPool
              </p>
            </div>
          </div>

          <div className="mt-4 flex space-x-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="isFeatured"
                checked={formData.isFeatured}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Featured Job</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="isUrgentHiring"
                checked={formData.isUrgentHiring}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Urgent Hiring</span>
            </label>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/hpanel/jobs')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-300 hover:bg-gray-50 hover:text-gray-600"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary text-white font-semibold py-2 px-6 rounded-lg"
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center">
                <i className="fas fa-spinner fa-spin mr-2"></i>
                {isEdit ? 'Updating...' : 'Creating...'}
              </span>
            ) : (
              isEdit ? 'Update Job' : 'Post Job'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default JobForm;